<template>
  <div>
    <v-breadcrumbs :items="items"></v-breadcrumbs>
    <v-card class="mb-5">
      <v-card-title>
        <v-container>
          <v-row>
            <v-col cols="12" md="2" class="mr-5 mb-1">
              Invoice Due Collection
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
    </v-card>
    <template v-if="isInvoiceVoid === 'void'">
      <div>
        <v-alert
            type="error"
            elevation="2"
        >
          This invoice has been voided. Collecting the due amount for this invoice is not permitted.
        </v-alert>
      </div>
    </template>
    <v-card :loading="loadingStatus">
      <v-container>
        <v-row>
          <!-- Left Column: Form Fields -->
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="headline">Invoice Due Collection</v-card-title>
              <v-card-text>
                <v-form ref="form" lazy-validation>
                  <!-- Invoice ID -->
                  <v-row>
                    <v-col cols="12" md="8">
                      <v-text-field
                          label="Invoice ID"
                          v-model="submitForm.invoiceId"
                          outlined
                          dense
                          required
                          readonly
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-checkbox
                          v-model="ifDiscountChecked"
                          label="Discount & Received"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <div v-show="ifDiscountChecked">
                    <v-row>
                      <v-col cols="12" md="12">
                        <v-autocomplete
                            label="Recommender Name"
                            v-model="submitForm.recommenderDoctor"
                            item-text="dr_name"
                            item-value="id"
                            :items="doctors"
                            outlined
                            dense
                            :loading="doctorLoadingStatus"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                            label="Discount(%)"
                            v-model="submitForm.discountPercentage"
                            outlined
                            dense
                            required
                            type="number"
                            @input="calculateDiscountFromPercentage"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                            label="Discount Amount"
                            v-model="submitForm.discountAmount"
                            outlined
                            dense
                            required
                            type="number"
                            @input="calculateDiscountFromAmount"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <v-text-field
                      label="Due Receive Amount"
                      v-model="submitForm.dueAmount"
                      type="number"
                      outlined
                      dense
                      required
                      readonly
                  ></v-text-field>

                  <v-textarea
                      label="Note"
                      v-model="submitForm.note"
                      outlined
                      dense
                      rows="2"
                  ></v-textarea>
                  <v-autocomplete
                      :items="paymentMethods"
                      v-model="submitForm.payment_method"
                      item-text="name"
                      item-value="name"
                      required
                      label="Payment Method"
                      outlined
                      dense
                  ></v-autocomplete>

                  <v-row>
                    <v-col cols="12" v-if="spinnerButtons">
                      <v-progress-circular :width="12" size="61" color="green" indeterminate
                                           style="margin-top: 5px; margin-bottom: 10px"></v-progress-circular>
                    </v-col>
                    <v-col cols="12" md="12" v-else>
                      <v-btn v-if="isInvoiceVoid !== 'void'" depressed color="success" :disabled="btnSubmit"
                             class="mx-2" @click="submitInvoiceDueCollection">
                        Submit
                      </v-btn>
                      <v-btn depressed color="info" v-if="this.due_collect_invoice_id" @click="dueCollectionInvoice">
                        <v-icon>mdi-printer</v-icon>
                        Due Collection Invoice Print
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column: Invoice Details -->
          <v-col cols="12" md="6">
            <!-- Invoice Information -->
            <v-card outlined class="mb-4">
              <!-- Invoice Details -->
              <v-card-title class="headline">Invoice Information: {{ invoiceData.invoice_id }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                        @click="invoice_print()"
                        dark
                        v-bind="attrs"
                        v-on="on"
                        class="ma-2"
                        open
                        prepend-icon="mdi-printer"
                        color="warning"
                        label
                        outlined
                    >
                      <v-icon> mdi-printer</v-icon>
                    </v-chip>
                  </template>
                  <span>Invoice Print</span>
                </v-tooltip>
              </v-card-title>
              <v-card-text>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Invoice Type: <strong>{{ invoiceData.invoice_type }}</strong>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Create Date: <span>{{ invoiceData.inv_date }}</span></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Total Amount: <span>{{ invoiceData.sub_total || '0.00' }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Discount(%): <span>{{ invoiceData.discount || '0.00' }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Discount Amount: <span>{{ invoiceData.discount_amount || '0.00' }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Paid Amount: <span>{{ invoiceData.paid_amount || '0.00' }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Status:
                        <v-btn outlined x-small class="warning">{{ invoiceData.status }}</v-btn>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>


              <!-- Doctor Information -->
              <v-card-subtitle class="mt-0">Doctor Information</v-card-subtitle>
              <v-card-text class="py-0" v-if="invoiceData.doctor">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Doctor Name:</v-list-item-title>
                      <v-list-item-subtitle>{{
                          invoiceData.doctor.dr_name
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Doctor Id:</v-list-item-title>
                      <v-list-item-subtitle>{{
                          invoiceData.doctor.dr_id
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
              <v-card-text class="py-0" v-if="invoiceData.refer_doctor">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Doctor Name:</v-list-item-title>
                      <v-list-item-subtitle v-if="invoiceData.refer_doctor">{{
                           invoiceData.refer_doctor.dr_name
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Doctor Id:</v-list-item-title>
                      <v-list-item-subtitle v-if="invoiceData.refer_doctor">{{
                           invoiceData.refer_doctor.dr_id
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>


              <!-- Patient Information -->
              <v-card-subtitle v-if="invoiceData.patient" class="mt-0">Patient Information</v-card-subtitle>
              <v-card-text class="py-0" v-if="invoiceData.patient">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Patient ID:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.patient.patient_id }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Patient Name:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.patient.patient_name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>

            </v-card>
          </v-col>

        </v-row>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import atshq from "../../../js/setup";

export default {
  data() {
    return {
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Invoice Due Collection",
          disabled: true,
        },
      ],
      spinnerButtons: true,
      btnSubmit: false,
      loadingStatus: true,
      doctorLoadingStatus: true,
      isInvoiceVoid: true,
      invoiceData: [],
      doctors: [],
      ifDiscountChecked: false,
      invoice_id: this.$route.params.InvoiceId,
      due_collect_invoice_id: '',
      returnApiBaseUrl: atshq.defaults.headers.common.api_base_URL,
      submitForm: {
        invoiceId: '',
        recommenderDoctor: '',
        dueAmount: 0,
        discountPercentage: 0,
        discountAmount: 0,
        note: '',
        payment_method: 'Cash',
        login_user_id: localStorage.getItem("userId"),
        local_ip_address: localStorage.getItem("local_ip_address"),
      },

      invoiceDueData: [],
      paymentMethods: [],
    };
  },
  created() {
    this.initialize();
    this.getDoctors();
    this.getPaymentMethods();
  },
  watch: {
    ifDiscountChecked(newValue) {
      if (!newValue) {
        this.refreshSubmitData();
        this.initialize();
      }
    }
  },
  methods: {
    initialize() {
      atshq
          .get("invoices/invoice-due-get-information/" + this.invoice_id)
          .then((response) => {
            this.loadingStatus = true;
            this.invoiceData = response.data.invoiceData;
            this.invoiceDueData = response.data.invoiceDueData;
            this.submitForm.dueAmount = this.invoiceDueData.due_amount;
            this.isInvoiceVoid = this.invoiceData.status;
            this.loadingStatus = false;
            this.spinnerButtons = false;
            this.getSubmitData(this.invoiceData);
          })
          .catch((e) => {
            this.$noty.error(e.response.data.message);
          });
    },
    getPaymentMethods() {
      atshq
          .get("/payment-methods")
          .then((response) => {
            this.paymentMethods = response.data.data;
          })
          .catch((e) => {
            this.$noty.error(e.response.data.message);
          });
    },
    getDoctors() {
      atshq
          .get("/doctors")
          .then((response) => {
            this.doctors = response.data.data;
            this.doctorLoadingStatus = false
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getSubmitData(invoiceData) {
      this.submitForm.invoiceId = invoiceData.invoice_id;
      this.submitForm.recommenderDoctor;
    },

    calculateDiscountFromPercentage() {
      const dueAmount = this.submitForm.dueAmount = this.invoiceDueData.due_amount;
      if (this.submitForm.discountPercentage < 0 || this.submitForm.discountPercentage > 100) {
        this.$noty.error('Discount percentage cannot exceed 100.');
        this.submitForm.discountPercentage = 0;
      }
      const discount = (dueAmount * this.submitForm.discountPercentage) / 100;
      this.submitForm.discountAmount = discount.toFixed(2);
      this.submitForm.dueAmount = (dueAmount - discount).toFixed(2);

    },
    calculateDiscountFromAmount() {
      const dueAmount = this.invoiceDueData.due_amount;
      if (this.submitForm.discountAmount > dueAmount || this.submitForm.discountAmount < 0) {
        this.submitForm.discountAmount = 0;
        this.submitForm.discountPercentage = 0;
        this.submitForm.dueAmount = dueAmount;
        this.$noty.error('Discount Amount cannot exceed Due Amount.');
        return;
      }
      const percentage = (this.submitForm.discountAmount / dueAmount) * 100;
      this.submitForm.discountPercentage = percentage.toFixed(2);
      this.submitForm.dueAmount = (dueAmount - this.submitForm.discountAmount).toFixed(2);
    },
    refreshSubmitData() {
      this.submitForm.recommenderDoctor = '';
      this.submitForm.discountPercentage = 0;
      this.submitForm.discountAmount = 0;
    },
    submitInvoiceDueCollection() {
      if (this.submitForm.dueAmount === null || this.submitForm.dueAmount === '' || this.submitForm.dueAmount <= 0) {
        this.$noty.error('Due amount cannot be less than or equal to 0 or empty.');
        return;
      }
      if (this.ifDiscountChecked === true) {
        if (this.submitForm.recommenderDoctor === null || this.submitForm.recommenderDoctor === '' || this.submitForm.recommenderDoctor <= 0) {
          this.$noty.error('Recommender doctor is required. Please select a doctor.');
          return;
        }
      }
      if (this.submitForm.note === null || this.submitForm.note === '') {
        this.$noty.error('Invoice note is required. Please provide a valid note.');
        return;
      }
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        type: 'success',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, submit it!'
      }).then((result) => {
        if (result.value) {
          this.btnSubmit = true
          this.spinnerButtons = true;
          this.loadingStatus = true
          let submitUrl = "invoices/invoice-due-collection-save";
          atshq
              .post(submitUrl, {submitData: this.submitForm})
              .then((response) => {
                this.$noty.success(response.data.message);
                this.spinnerButtons = false;
                this.due_collect_invoice_id = response.data.invoice_id;
                this.btnSubmit = false;
                this.initialize();
                this.refreshSubmitData();
                this.loadingStatus = false
              })
              .catch((e) => {
                this.btnSubmit = false
                this.$noty.error(e.response.data.message);
                this.spinnerButtons = false;
                this.initialize();
                this.refreshSubmitData();
              });
        }
      });
    },
    invoice_print() {
      window.open(this.returnApiBaseUrl + "print/invoice/" + this.invoiceData.invoice_id);
    },
    dueCollectionInvoice() {
      window.open(this.returnApiBaseUrl + "print/invoice/" + this.due_collect_invoice_id);
    }
  },
};
</script>0

<style scoped>
.headline {
  font-weight: bold;
  color: #2c3e50;
}
</style>